import { useState } from "react";

const formatTime = (seconds) => {
  if (seconds == null) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};
// 🕒 "last seen" formatter for the chat header subtitle
const formatLastSeen = (lastSeenAt) => {
  if (!lastSeenAt) return null;
  const date = new Date(lastSeenAt);
  const now  = new Date();
  const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const isToday =
    date.getDate()    === now.getDate() &&
    date.getMonth()   === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isToday)      return `last seen today at ${timeStr}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate()    === yesterday.getDate() &&
    date.getMonth()   === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isYesterday) return `last seen yesterday at ${timeStr}`;
  const dateStr = date.toLocaleDateString([], { month: "short", day: "numeric" });
  return `last seen ${dateStr} at ${timeStr}`;
};

export default function ChatHeader({
  selectedItem,
  groupMembers,
  liveGroup,
  renderTypingForSelected,
  p2pOrder,
  remainingTime,
  showCallButtons,
  onBack,
  onAudioCall,
  onVideoCall,
  myChatId,
  socket,
  showToast,
  onOpenGroupInfo,
  onOpenUserInfo,
  onExitGroup,
  onClearChat,
  mobileView,
}) {
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);

  if (!selectedItem) return null;

  /* ── Resolved display label ── */
  const displayName =
    selectedItem.type === "user"
      ? selectedItem.displayName || selectedItem.username || "User"
      : selectedItem.name || "Group";

  const handleAvatarOrNameClick = () => {
    if (selectedItem.type === "group") onOpenGroupInfo();
    else if (selectedItem.type === "user") onOpenUserInfo();
  };

  return (
    <div
      className="
        relative flex items-center
        px-1 sm:px-3 py-2 border-b cursor-pointer
        bg-white dark:bg-neutral-900
        border-neutral-200 dark:border-neutral-800
        min-h-[56px] sm:min-h-[60px]
        gap-1
      "
      style={mobileView ? { paddingTop: "max(8px, env(safe-area-inset-top, 8px))" } : {}}
    >
      {/* ── BACK BUTTON (mobile only) ── */}
      {mobileView && onBack && (
        <button
          onClick={(e) => { e.stopPropagation(); onBack(); }}
          className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
      )}

      {/* ── AVATAR + NAME + STATUS (clickable) ── */}
      <div
        className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1"
        onClick={handleAvatarOrNameClick}
      >
        {/* AVATAR */}
        <div className="relative w-9 h-9 sm:w-10 sm:h-10 shrink-0">
          {selectedItem.type === "group" ? (
            selectedItem.groupImageUrl ? (
              <img
                src={selectedItem.groupImageUrl}
                alt="Group avatar"
                className="w-full h-full rounded-full object-cover border border-neutral-300 dark:border-neutral-700"
              />
            ) : (
              <div className="w-full h-full rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-lg">
                👥
              </div>
            )
          ) : selectedItem.profileImageUrl ? (
            <img
              src={selectedItem.profileImageUrl}
              alt={displayName}
              className="w-full h-full rounded-full object-cover border border-neutral-300 dark:border-neutral-700"
            />
          ) : (
            <div className="w-full h-full rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-lg">
              👤
            </div>
          )}

          {/* Online dot (DM only) */}
          {selectedItem.type === "user" && (
            <span
              className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white dark:border-neutral-900 ${
                selectedItem.online ? "bg-green-500" : "bg-neutral-400"
              }`}
            />
          )}
        </div>

      {/* NAME + TYPING/STATUS */}
        <div className="flex flex-col min-w-0">
          <div className="font-semibold truncate text-sm sm:text-base text-neutral-900 dark:text-neutral-100">
            {displayName}
          </div>
          <div className="text-xs truncate text-neutral-500 dark:text-neutral-400">
            {renderTypingForSelected() ||
              (selectedItem.type === "user"
                ? selectedItem.online
                  ? "online"
                  : formatLastSeen(selectedItem.lastSeenAt)
                : null)}
          </div>
        </div>
      </div>

      {/* ── RIGHT SIDE ── */}
      <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-1">

        {/* P2P order info (DM only) */}
        {selectedItem.type === "user" && p2pOrder && (
          <div className="flex flex-col items-center gap-0.5" onClick={handleAvatarOrNameClick}>
            <div className="text-[10px] sm:text-[11px] font-semibold text-blue-600 dark:text-blue-400 whitespace-nowrap">
              #{p2pOrder.orderId}
            </div>
            {remainingTime != null && remainingTime > 0 && (
              <div className="text-[10px] sm:text-[11px] whitespace-nowrap text-amber-600 dark:text-amber-400">
                {String(p2pOrder.buyerChatId) === String(myChatId)
                  ? `Pay ${(Number(p2pOrder.amount || 0) * Number(p2pOrder.rate || 0)).toLocaleString()} ${p2pOrder.currency || ""} for ${p2pOrder.amount} ${p2pOrder.asset} within ${formatTime(remainingTime)}`
                  : `Buyer pays ${(Number(p2pOrder.amount || 0) * Number(p2pOrder.rate || 0)).toLocaleString()} ${p2pOrder.currency || ""} for ${p2pOrder.amount} ${p2pOrder.asset} in ${formatTime(remainingTime)}`}
              </div>
            )}
          </div>
        )}

        {/* Group member count (group only) */}
        {selectedItem.type === "group" && (
          <span
            className="text-xs text-neutral-500 dark:text-neutral-400 cursor-pointer"
            onClick={handleAvatarOrNameClick}
          >
            {groupMembers[selectedItem.groupId]?.length ||
              liveGroup?.members?.length ||
              0}{" "}
            members
          </span>
        )}

        {/* Call buttons (DM only) */}
        {showCallButtons && selectedItem.type === "user" && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onAudioCall(); }}
              title="Audio call"
              className="text-lg sm:text-xl p-1 text-neutral-600 dark:text-neutral-300 hover:text-green-600 active:scale-90 transition-transform touch-manipulation"
            >
              📞
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onVideoCall(); }}
              title="Video call"
              className="text-lg sm:text-xl p-1 text-neutral-600 dark:text-neutral-300 hover:text-blue-600 active:scale-90 transition-transform touch-manipulation"
            >
              🎥
            </button>
          </>
        )}

        {/* 3-dot menu button */}
        <button
          className="rounded-full px-2 py-1 text-2xl sm:text-3xl text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:bg-neutral-200 dark:active:bg-neutral-700 touch-manipulation leading-none"
          onClick={(e) => {
            e.stopPropagation();
            setShowHeaderMenu((v) => !v);
          }}
          aria-label="Chat menu"
        >
          ⋮
        </button>
      </div>

      {/* ── DROPDOWN ── */}
      {showHeaderMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => { e.stopPropagation(); setShowHeaderMenu(false); }}
          />
          <div
            className="
              absolute right-2 top-full mt-1 z-50 w-44 sm:w-48
              overflow-hidden rounded-xl border shadow-lg
              bg-white dark:bg-neutral-800
              border-neutral-200 dark:border-neutral-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            {selectedItem.type === "user" && (
              <>
                <button
                  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 active:bg-neutral-200 touch-manipulation"
                  onClick={() => {
                    setShowHeaderMenu(false);
                    if (typeof onClearChat === "function") onClearChat();
                  }}
                >
                  🧹 Clear Chat
                </button>
                <button
                  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 active:bg-neutral-200 touch-manipulation"
                  onClick={() => { setShowHeaderMenu(false); showToast("Report submitted"); }}
                >
                  ⚠️ Report
                </button>
                <button
                  className="w-full px-4 py-3 sm:py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 active:bg-red-100 touch-manipulation"
                  onClick={() => { setShowHeaderMenu(false); showToast("User blocked (placeholder)"); }}
                >
                  🚫 Block
                </button>
              </>
            )}

            {selectedItem.type === "group" && (
              <>
                <button
                  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 active:bg-neutral-200 touch-manipulation"
                  onClick={() => { setShowHeaderMenu(false); onOpenGroupInfo(); }}
                >
                  👥 Group Info
                </button>
                <button
                  className="w-full px-4 py-3 sm:py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 active:bg-neutral-200 touch-manipulation"
                  onClick={() => {
                    setShowHeaderMenu(false);
                    if (typeof onClearChat === "function") onClearChat();
                  }}
                >
                  🧹 Clear Chat
                </button>
                <button
                  className="w-full px-4 py-3 sm:py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 active:bg-red-100 touch-manipulation"
                  onClick={() => {
                    setShowHeaderMenu(false);
                    if (!window.confirm("Exit group?")) return;
                    onExitGroup();
                  }}
                >
                  🚪 Exit Group
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}